import { Link, useNavigate } from "react-router-dom";
import { Paper, Title, Text, Loader, Center, Button, Container, Divider, Group, ScrollArea, Space, Stack, ThemeIcon, Menu, Badge, useMantineTheme, ActionIcon, SimpleGrid } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { IconPencil, IconDots, IconTrash, IconChevronLeft, IconMapPin, IconUsers } from "@tabler/icons-react";
import moment from "moment";

import { useAccess } from "../../Auth/auth-functions";
import { AzureADRoles } from "../../Auth/auth-roles";
import { LocationDto } from "../../models/location.models";
import { LocationWorkOrderList } from "./LocationWorkOrderList";
import { ListWorkOrderDto } from "../../models/workorder.models";
import { CrudUserDate } from "../Shared/CrudUserDate";
import classes from './LocationDetailView.module.css';

interface LocationDetailViewProps {
	location?: LocationDto | null;
	isLoading?: boolean;
	onEdit?: () => void;
	onDelete?: (location: LocationDto) => void;
	onClose?: () => void;
}

export const LocationDetailView = ({ location, isLoading, onEdit, onDelete, onClose }: LocationDetailViewProps) => {
	const navigate = useNavigate();
	const theme = useMantineTheme();
	const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.xs})`, true);
	const canEditLocation = useAccess(AzureADRoles.Admin);

	const handleWorkOrderSelected = (workOrder: ListWorkOrderDto): void => {
		navigate(`/workorders/${workOrder.WorkOrderId}`);
	};

	const deleteLocation = () => {
		if (!location) return;

		if (onDelete && location)
			onDelete(location);
	}

	if (isLoading) return <Paper withBorder={!isMobile} p={0} radius={isMobile ? "0" : "md"} style={{ overflow: 'hidden' }}>
			<ScrollArea type={isMobile ? "never" : "scroll"} style={{ height: isMobile ? 'unset' : 'calc(100vh - 160px)' }}>
				<Center>
					<Loader size="sm" mt="lg" />
				</Center>
			</ScrollArea>
		</Paper>
	if (!location) return <Paper withBorder={!isMobile} p={0} radius={isMobile ? "0" : "md"} style={{ overflow: 'hidden' }}>
			<ScrollArea type={isMobile ? "never" : "scroll"} style={{ height: isMobile ? 'unset' : 'calc(100vh - 160px)' }}>
				<Space h="md" />
				<Stack justify="center" align="center" gap="md" pt="lg">
					<ThemeIcon size={100} radius={100} variant="light" color="gray">
						<IconMapPin size={60} />
					</ThemeIcon>
					<Text fz="lg" fw="500" c="dimmed">Select a location to view details</Text>
				</Stack>
				<Space h="md" />
			</ScrollArea>
		</Paper>

	return <>
		<Paper withBorder={!isMobile} p={0} radius={isMobile ? "0" : "md"} style={{ overflow: 'hidden' }}>
			<ScrollArea type={isMobile ? "never" : "hover"} style={{ height: isMobile ? 'calc(100vh)' : 'calc(100vh - 160px)' }}>
				<Stack gap={0} mb="md" justify="center" className={classes['header']}>
					<Group justify="space-between" px="20" wrap="nowrap">
						<Group gap="xs" wrap="nowrap">
							<ActionIcon variant="light" radius="xl" onClick={onClose} hiddenFrom="sm">
								<IconChevronLeft />
							</ActionIcon>
							<Stack gap={0}>
								<Title order={isMobile ? 6 : 3}>{location?.Title}</Title>
								<Text fz="xs" c="dimmed">Created on {moment(location?.Created).format("MMMM D, YYYY")}</Text>
							</Stack>
						</Group>
						<Group gap="xs">
							<Button.Group mr={-10}>
								{!location.Deleted && <>
									{!isMobile &&
									<Button onClick={onEdit} disabled={!canEditLocation} variant="subtle" radius="xl" leftSection={<IconPencil stroke={1.5} />}>Edit</Button>
									}
									{isMobile &&
									<Button onClick={onEdit} disabled={!canEditLocation} variant="subtle" radius="xl" px="sm"><IconPencil stroke={1.5} /></Button>
									}
									<Divider orientation="vertical" />
									<Menu position="bottom-end" shadow="md" radius="lg">
										<Menu.Target>
											<Button disabled={!canEditLocation} variant="subtle" radius="xl" px="sm"><IconDots size={20} /></Button>
										</Menu.Target>
										<Menu.Dropdown>
											<Menu.Item onClick={deleteLocation} color="red" leftSection={<IconTrash stroke={1.5} />} fw="bold">Delete</Menu.Item>
										</Menu.Dropdown>
									</Menu>
								</>}
							</Button.Group>
						</Group>
					</Group>
				</Stack>

				{/* <Space h="md" />
				<Group justify="space-between" px="20">
					<Title order={3}>{location?.Title}</Title>
					<Button variant="outline" radius="md" leftSection={<IconPencil />} onClick={onEdit}>Edit</Button>
				</Group>
				<Space h="md" />
				<Divider mb="md" /> */}

				<Container px="20">
					{/* <Space h="md" /> */}
					{location?.Description && <>
					<Text fz="xs" fw="700" tt="uppercase" c="dimmed">{location?.Description}</Text>
					<Space h="md" />
					</>}

					{location?.Address && <>
					<Text fz="xs" fw="700" tt="uppercase" c="dimmed">Address</Text>
					<Space h="xs" />
					<Text fz="sm" pb="md">{location?.Address}</Text>
					</>}

					{location?.Teams.length > 0 && <>
						<Text fz="xs" fw="700" tt="uppercase" c="dimmed">Teams in Charge</Text>
						<Space h="xs" />
						<Group wrap="wrap" gap="xs">
							{location.Teams.map(team => (
								<Link key={team.TeamId} to={{ pathname: `/teams/${team.TeamId}` }} className={classes['link']}>
									<Badge size="lg" fz="sm" radius="sm" leftSection={<IconUsers size={14} />}>{team.Name}</Badge>
								</Link>
							))}
						</Group>
						<Divider my="md" />
					</>}

					<SimpleGrid cols={isMobile ? 1 : 2}>
						<CrudUserDate label="Created" userId={location.CreatedBy.UserId} userName={location.CreatedBy.Name} date={location.Created} />
						{location.UpdatedBy && !location.Deleted && <>
							<CrudUserDate label="Updated" userId={location.UpdatedBy.UserId} userName={location.UpdatedBy.Name} date={location.Updated} />
						</>}
						{location.DeletedBy && <>
							<CrudUserDate label="Deleted" userId={location.DeletedBy.UserId} userName={location.DeletedBy.Name} date={location.Deleted} />
						</>}
					</SimpleGrid>
					<Divider my="md" />

					<Text fz="xs" fw="700" tt="uppercase" c="dimmed">Work Order History</Text>
					<Space h="md" />
					<LocationWorkOrderList locationId={location.LocationId} onSelect={handleWorkOrderSelected} />
					<Space h="md" />
				</Container>
			</ScrollArea>
		</Paper>
	</>;
}
